// State badge for row-level lifecycle status — CTSK-204 (PatsPanel UI
// parity with the External Clients Nest page). Color-coded pill mirroring
// ExternalClientsNest.tsx's inline access-state badges (see its
// ACCESS_COLORS map), generalized here as its own component since
// PatsPanel's state vocabulary (active / expiring-soon / expired /
// revoked) is a different axis than the Nest's (pending / approved /
// denied / revoked) — colors per CTSK-204's ticket spec, not copied
// from the Nest's own mapping.
//
// NOTE: GET /pats server-side excludes revoked tokens entirely
// (apps/api/src/routes/pats.ts — `isNull(personalAccessTokens.revokedAt)`
// in the list query), so `revoked` never actually renders in this app
// today. The state + color stay here for forward-compatibility (e.g. if
// a future admin-scoped list endpoint surfaces revoked rows) and because
// the ticket's tab strip + badge spec calls for it either way — flagged
// in the CTSK-204 ship comment.

export type PatState = 'active' | 'expiring_soon' | 'expired' | 'revoked';

const STATE_COLORS: Record<PatState, string> = {
  active: '#22C55E',
  expiring_soon: '#F59E0B',
  expired: '#6B7280',
  revoked: '#EF4444',
};

const STATE_LABELS: Record<PatState, string> = {
  active: 'ACTIVE',
  expiring_soon: 'EXPIRING SOON',
  expired: 'EXPIRED',
  revoked: 'REVOKED',
};

/** 30-day window for "expiring soon" — matches the ticket's delta 3 spec. */
const EXPIRING_SOON_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export function patState(params: {
  revokedAt?: string | null;
  expiresAt?: string | null;
  now?: number;
}): PatState {
  if (params.revokedAt) return 'revoked';
  const now = params.now ?? Date.now();
  if (params.expiresAt) {
    const expMs = new Date(params.expiresAt).getTime();
    if (!Number.isNaN(expMs)) {
      if (expMs < now) return 'expired';
      if (expMs - now <= EXPIRING_SOON_WINDOW_MS) return 'expiring_soon';
    }
  }
  return 'active';
}

export function StateBadge({ state }: { state: PatState }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        background: STATE_COLORS[state] + '20',
        color: STATE_COLORS[state],
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        whiteSpace: 'nowrap',
      }}
    >
      {STATE_LABELS[state]}
    </span>
  );
}
